import React, { useState } from "react";
import type { EmployeePointsCalculation } from "../modules/points/types";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { formatShortDate } from "@/utils/formatDate";

type SparkChartProps = {
  periods: EmployeePointsCalculation[];
  globalMax?: number;
};

const SPARK_WIDTH = 140;
const SPARK_HEIGHT = 54;
const SPARK_PADDING = 6;
const DOT_RADIUS = 3;
const GAP_COLOR = "var(--color-muted-foreground)";
const POSITIVE_COLOR = "var(--color-highlight-600)";
const LINE_COLOR = "var(--color-highlight-700)";

function buildSmoothPath(points: { x: number; y: number }[]): string {
  if (points.length < 2) return "";
  if (points.length === 2) {
    return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
  }

  const tension = 0.2;
  let d = `M ${points[0].x},${points[0].y}`;

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(i - 1, 0)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(i + 2, points.length - 1)];

    const cp1x = p1.x + (p2.x - p0.x) * tension;
    const cp1y = p1.y + (p2.y - p0.y) * tension;
    const cp2x = p2.x - (p3.x - p1.x) * tension;
    const cp2y = p2.y - (p3.y - p1.y) * tension;

    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }

  return d;
}

const SparkChart: React.FC<SparkChartProps> = ({ periods, globalMax }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  if (periods.length === 0) return null;

  const scoredValues = periods
    .map((p) => p.totalPoints)
    .filter((v): v is number => v !== null);
  if (scoredValues.length === 0) return null;

  const effectiveMax = (globalMax && globalMax > 0)
    ? globalMax
    : Math.max(...scoredValues) || 1;

  const chartTop = SPARK_PADDING;
  const chartBottom = SPARK_HEIGHT - SPARK_PADDING;
  const chartRange = chartBottom - chartTop;

  const refLineY = (value: number) =>
    chartTop + (1 - value / effectiveMax) * chartRange;

  const zeroY = chartBottom;
  const midY = refLineY(effectiveMax / 2);
  const highY = chartTop;

  const coords = periods.map((p, i) => {
    const x =
      SPARK_PADDING +
      (periods.length === 1
        ? (SPARK_WIDTH - 2 * SPARK_PADDING) / 2
        : (i / (periods.length - 1)) * (SPARK_WIDTH - 2 * SPARK_PADDING));

    const scored = p.totalPoints !== null;
    const y = scored ? refLineY(p.totalPoints) : zeroY;

    return { x, y, scored };
  });

  // Build separate path segments for consecutive positive runs — zero-day
  // scores break the line just like missing days do.
  const segments: string[] = [];
  let run: { x: number; y: number }[] = [];

  for (let i = 0; i < coords.length; i++) {
    if (coords[i].scored && periods[i].totalPoints > 0) {
      run.push(coords[i]);
    } else {
      if (run.length >= 2) segments.push(buildSmoothPath(run));
      run = [];
    }
  }
  if (run.length >= 2) segments.push(buildSmoothPath(run));

  const hitRects = coords.map((p, i) => {
    const prevX = i > 0 ? coords[i - 1].x : 0;
    const nextX = i < coords.length - 1 ? coords[i + 1].x : SPARK_WIDTH;
    const left = i === 0 ? 0 : (prevX + p.x) / 2;
    const right =
      i === coords.length - 1 ? SPARK_WIDTH : (p.x + nextX) / 2;
    return { x: left, y: 0, width: right - left, height: SPARK_HEIGHT };
  });

  return (
    <svg
      width={SPARK_WIDTH}
      height={SPARK_HEIGHT}
      viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
      className="shrink-0"
      onMouseLeave={() => setHoveredIndex(null)}
    >
      {[zeroY, midY, highY].map((y, i) => (
        <line
          key={`ref-${i}`}
          x1={0}
          y1={y}
          x2={SPARK_WIDTH}
          y2={y}
          stroke={GAP_COLOR}
          strokeWidth={0.5}
          strokeDasharray={i === 0 ? "0 0" : "2 3"}
          opacity={0.4}
        />
      ))}
      {segments.map((d, i) => (
        <path
          key={`seg-${i}`}
          d={d}
          fill="none"
          stroke={LINE_COLOR}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
      {coords.map((p, i) => (
        <circle
          key={`dot-${i}`}
          cx={p.x}
          cy={p.y}
          r={1.5}
          fill={periods[i].totalPoints > 0 ? POSITIVE_COLOR : GAP_COLOR}
          opacity={hoveredIndex === i ? 0 : p.scored ? 0.45 : 0.3}
          pointerEvents="none"
        />
      ))}
      {hitRects.map((rect, i) => (
        <Tooltip key={periods[i].id} open={hoveredIndex === i}>
          <TooltipTrigger asChild>
            <rect
              x={rect.x}
              y={rect.y}
              width={rect.width}
              height={rect.height}
              fill="transparent"
              className="cursor-pointer"
              onMouseEnter={() => setHoveredIndex(i)}
            />
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={4}>
            {periods[i].totalPoints !== null && periods[i].totalPoints > 0 ? (
              <>
                <span className="font-bold tabular-nums">
                  {periods[i].totalPoints} pts
                </span>
                <span className="opacity-70">
                  {" "}
                  {formatShortDate(periods[i].calculatedOn)}
                </span>
              </>
            ) : periods[i].totalPoints === 0 ? (
              <>
                <span className="text-muted-foreground">0 pts</span>
                <span className="opacity-70">
                  {" "}
                  {formatShortDate(periods[i].calculatedOn)}
                </span>
              </>
            ) : (
              <span className="opacity-70">
                0 pts · {formatShortDate(periods[i].calculatedOn)}
              </span>
            )}
          </TooltipContent>
        </Tooltip>
      ))}
      {hoveredIndex !== null && (
        <circle
          cx={coords[hoveredIndex].x}
          cy={coords[hoveredIndex].y}
          r={DOT_RADIUS}
          fill={
            periods[hoveredIndex].totalPoints > 0 ? POSITIVE_COLOR : GAP_COLOR
          }
          stroke="none"
          pointerEvents="none"
        />
      )}
    </svg>
  );
};

export default SparkChart;
